import type {
  ProviderConfig,
  ProviderId,
  ThinkingCapability,
  ThinkingEffort,
  ThinkingLevel,
  ThinkingRequestAdapter,
} from '@/lib/types/provider';

export function getModelMetadataKey(providerId: string, modelId: string): string {
  return `${providerId}:${modelId}`;
}

function effortCapability(
  requestAdapter: ThinkingRequestAdapter,
  effortValues: ThinkingEffort[],
  defaultEffort: ThinkingEffort,
): ThinkingCapability {
  return {
    control: 'effort',
    requestAdapter,
    effortValues,
    defaultEffort,
    defaultMode: effortValues.includes('none') ? 'disabled' : 'enabled',
    toggleable: effortValues.includes('none'),
    budgetAdjustable: true,
    defaultEnabled: !effortValues.includes('none'),
  };
}

function levelCapability(
  levelValues: ThinkingLevel[],
  defaultLevel: ThinkingLevel,
): ThinkingCapability {
  return {
    control: 'level',
    requestAdapter: 'google',
    levelValues,
    defaultLevel,
    defaultMode: 'enabled',
    toggleable: false,
    budgetAdjustable: true,
    defaultEnabled: true,
  };
}

function toggleCapability(
  requestAdapter: ThinkingRequestAdapter,
  defaultEnabled = true,
): ThinkingCapability {
  return {
    control: 'toggle',
    requestAdapter,
    defaultMode: defaultEnabled ? 'enabled' : 'disabled',
    toggleable: true,
    budgetAdjustable: false,
    defaultEnabled,
  };
}

function toggleBudgetCapability(
  requestAdapter: ThinkingRequestAdapter,
  range: { min: number; max: number; step?: number; allowDynamic?: boolean; disableValue?: number },
  defaultEnabled = false,
  defaultBudgetTokens?: number,
): ThinkingCapability {
  return {
    control: 'toggle-budget',
    requestAdapter,
    budgetRange: range,
    defaultBudgetTokens,
    defaultMode: defaultEnabled ? 'enabled' : 'disabled',
    toggleable: true,
    budgetAdjustable: true,
    defaultEnabled,
  };
}

function budgetOnlyCapability(
  requestAdapter: ThinkingRequestAdapter,
  range: { min: number; max: number; step?: number; allowDynamic?: boolean },
  defaultBudgetTokens?: number,
): ThinkingCapability {
  return {
    control: 'budget-only',
    requestAdapter,
    budgetRange: range,
    defaultBudgetTokens,
    defaultMode: 'enabled',
    toggleable: false,
    budgetAdjustable: true,
    defaultEnabled: true,
  };
}

const fixedThinkingCapability: ThinkingCapability = {
  control: 'none',
  requestAdapter: 'none',
  defaultMode: 'enabled',
  toggleable: false,
  budgetAdjustable: false,
  defaultEnabled: true,
};

const anthropicManualBudgetByEffort: Partial<Record<ThinkingEffort, number>> = {
  low: 4096,
  medium: 10240,
  high: 32768,
  max: 64000,
};

const anthropicManualEffort: ThinkingCapability = {
  control: 'effort',
  requestAdapter: 'anthropic',
  effortValues: ['none', 'low', 'medium', 'high', 'max'],
  defaultEffort: 'medium',
  defaultMode: 'enabled',
  toggleable: true,
  budgetAdjustable: true,
  defaultEnabled: true,
  anthropicThinking: {
    type: 'enabled',
    budgetByEffort: anthropicManualBudgetByEffort,
  },
};

const anthropicAdaptiveEffort: ThinkingCapability = {
  ...anthropicManualEffort,
  anthropicThinking: { type: 'adaptive' },
};

const anthropicBudget: ThinkingCapability = toggleBudgetCapability(
  'anthropic',
  { min: 1024, max: 64000, step: 1024 },
  false,
  1024,
);

const anthropicOpus47Effort: ThinkingCapability = {
  ...anthropicAdaptiveEffort,
  effortValues: ['none', 'low', 'medium', 'high', 'xhigh', 'max'],
};

const deepseekEffort: ThinkingCapability = {
  control: 'effort',
  requestAdapter: 'deepseek',
  effortValues: ['none', 'high', 'max'],
  defaultEffort: 'high',
  defaultMode: 'enabled',
  toggleable: true,
  budgetAdjustable: true,
  defaultEnabled: true,
};

const glm52Effort: ThinkingCapability = {
  control: 'effort',
  requestAdapter: 'glm',
  effortValues: ['none', 'minimal', 'low', 'medium', 'high', 'xhigh', 'max'],
  defaultEffort: 'max',
  defaultMode: 'enabled',
  toggleable: true,
  budgetAdjustable: true,
  defaultEnabled: true,
};

const hunyuanHy3Effort: ThinkingCapability = {
  control: 'effort',
  requestAdapter: 'hunyuan',
  effortValues: ['none', 'low', 'high'],
  defaultEffort: 'none',
  defaultMode: 'disabled',
  toggleable: true,
  budgetAdjustable: true,
  defaultEnabled: false,
};

// 【B 3 v7.7.6】启用 lemonade provider (Phase C vendor)
// 本地 GGUF 模型推理 (Qwen3, GPT-OSS, Gemma)
const lemonadeToggleBudget = toggleBudgetCapability(
  'lemonade',
  { min: 0, max: 81920, step: 1024, disableValue: 0 },
  false,
);

const qwenBudgetEnabled = toggleBudgetCapability(
  'qwen',
  { min: 0, max: 81920, step: 1024, disableValue: 0 },
  true,
);

const qwenBudgetDisabled = toggleBudgetCapability(
  'qwen',
  { min: 0, max: 81920, step: 1024, disableValue: 0 },
  false,
);

const siliconflowBudget = budgetOnlyCapability(
  'siliconflow',
  { min: 128, max: 32768, step: 1024 },
  4096,
);

const siliconflowToggleBudget = toggleBudgetCapability(
  'siliconflow',
  { min: 128, max: 32768, step: 1024, disableValue: 0 },
  true,
  4096,
);

const doubaoMode: ThinkingCapability = {
  control: 'mode',
  requestAdapter: 'doubao',
  defaultMode: 'auto',
  toggleable: true,
  budgetAdjustable: false,
  defaultEnabled: true,
};

const doubaoSeed20Effort: ThinkingCapability = {
  control: 'effort',
  requestAdapter: 'doubao',
  effortValues: ['minimal', 'low', 'medium', 'high'],
  defaultEffort: 'medium',
  defaultMode: 'enabled',
  toggleable: true,
  budgetAdjustable: true,
  defaultEnabled: true,
};

const minimaxM3Thinking = toggleCapability('anthropic', false);

const THINKING_CAPABILITIES: Record<string, ThinkingCapability> = {
  [getModelMetadataKey('openai', 'gpt-5.5')]: effortCapability(
    'openai',
    ['low', 'medium', 'high', 'xhigh'],
    'medium',
  ),
  [getModelMetadataKey('openai', 'gpt-5.4-pro')]: effortCapability(
    'openai',
    ['medium', 'high', 'xhigh'],
    'medium',
  ),
  [getModelMetadataKey('openai', 'gpt-5.4')]: effortCapability(
    'openai',
    ['none', 'low', 'medium', 'high', 'xhigh'],
    'none',
  ),
  [getModelMetadataKey('openai', 'gpt-5.4-mini')]: effortCapability(
    'openai',
    ['none', 'low', 'medium', 'high', 'xhigh'],
    'none',
  ),
  [getModelMetadataKey('openai', 'gpt-5.4-nano')]: effortCapability(
    'openai',
    ['none', 'low', 'medium', 'high', 'xhigh'],
    'none',
  ),

  [getModelMetadataKey('anthropic', 'claude-opus-4-8')]: anthropicOpus47Effort,
  [getModelMetadataKey('anthropic', 'claude-opus-4-7')]: anthropicOpus47Effort,
  [getModelMetadataKey('anthropic', 'claude-opus-4-6')]: anthropicAdaptiveEffort,
  [getModelMetadataKey('anthropic', 'claude-sonnet-4-6')]: anthropicAdaptiveEffort,
  [getModelMetadataKey('anthropic', 'claude-sonnet-4-5')]: anthropicManualEffort,
  [getModelMetadataKey('anthropic', 'claude-haiku-4-5')]: anthropicBudget,

  [getModelMetadataKey('google', 'gemini-3.5-flash')]: levelCapability(
    ['minimal', 'low', 'medium', 'high'],
    'medium',
  ),
  [getModelMetadataKey('google', 'gemini-3.1-pro-preview')]: levelCapability(
    ['minimal', 'low', 'medium', 'high'],
    'high',
  ),
  [getModelMetadataKey('google', 'gemini-3-flash-preview')]: levelCapability(
    ['minimal', 'low', 'medium', 'high'],
    'high',
  ),
  [getModelMetadataKey('google', 'gemini-2.5-flash')]: toggleBudgetCapability(
    'google',
    { min: 0, max: 24576, step: 1024, allowDynamic: true, disableValue: 0 },
    true,
    -1,
  ),
  [getModelMetadataKey('google', 'gemini-2.5-flash-lite')]: toggleBudgetCapability(
    'google',
    { min: 0, max: 24576, step: 1024, allowDynamic: true, disableValue: 0 },
    false,
    0,
  ),
  [getModelMetadataKey('google', 'gemini-2.5-pro')]: budgetOnlyCapability(
    'google',
    { min: 128, max: 32768, step: 1024, allowDynamic: true },
    -1,
  ),

  [getModelMetadataKey('glm', 'glm-5.2')]: glm52Effort,
  // 【v6.97.125 2026-09-12 P0 修复】glm-5.3-flash 未注册 → getCatalogThinkingCapability 返回 undefined → fetch wrapper
  //   不注入 thinking:disabled → GLM 默认 thinking ON → 短 prompt 时 10 tokens 全被 reasoning 吃掉 → content 空
  //   → Next.js JSON 解析失败 → retries 30s+ → nginx 504 → 浏览器看到 "network error"
  //   实测: 短 prompt (max_tokens=50) 时 GLM 返 reasoning_tokens=10 content="" finish_reason=length
  //         加 thinking:{type:"disabled"} 后 1.2s 返 "Hi there! How can I help you today?"
  // 修法: 注册 glm-5.3-flash 用 toggleCapability('glm', false) → defaultMode='disabled'
  //   admin 后台 cfg 里 thinkingMode=default + thinkingEnabled=false 也走 disabled 路径
  [getModelMetadataKey('glm', 'glm-5.3-flash')]: toggleCapability('glm', false),
  [getModelMetadataKey('glm', 'glm-5.1')]: toggleCapability('glm', false),
  [getModelMetadataKey('glm', 'glm-5v-turbo')]: toggleCapability('glm', false),
  [getModelMetadataKey('glm', 'glm-5')]: toggleCapability('glm', false),
  [getModelMetadataKey('glm', 'glm-4.7')]: toggleCapability('glm', false),
  [getModelMetadataKey('glm', 'glm-4.7-flashx')]: toggleCapability('glm', false),
  [getModelMetadataKey('glm', 'glm-4.7-flash')]: toggleCapability('glm', false),
  [getModelMetadataKey('glm', 'glm-4.6')]: toggleCapability('glm', false),
  [getModelMetadataKey('glm', 'glm-4.6v')]: toggleCapability('glm', false),
  [getModelMetadataKey('glm', 'glm-4.6v-flash')]: toggleCapability('glm', false),

  [getModelMetadataKey('qwen', 'qwen3.7-plus')]: qwenBudgetEnabled,
  [getModelMetadataKey('qwen', 'qwen3.7-max')]: qwenBudgetEnabled,
  [getModelMetadataKey('qwen', 'qwen3.6-max-preview')]: qwenBudgetDisabled,
  [getModelMetadataKey('qwen', 'qwen3.6-plus')]: qwenBudgetEnabled,
  [getModelMetadataKey('qwen', 'qwen3.6-plus-2026-04-02')]: qwenBudgetEnabled,
  [getModelMetadataKey('qwen', 'qwen3.6-flash')]: qwenBudgetEnabled,
  [getModelMetadataKey('qwen', 'qwen3.6-flash-2026-04-16')]: qwenBudgetEnabled,
  [getModelMetadataKey('qwen', 'qwen3.6-35b-a3b')]: qwenBudgetEnabled,
  [getModelMetadataKey('qwen', 'qwen3.5-flash')]: qwenBudgetEnabled,
  [getModelMetadataKey('qwen', 'qwen3.5-plus')]: qwenBudgetEnabled,
  [getModelMetadataKey('qwen', 'qwen3-max')]: qwenBudgetDisabled,
  [getModelMetadataKey('qwen', 'qwen3-vl-plus')]: qwenBudgetDisabled,

  [getModelMetadataKey('deepseek', 'deepseek-v4-pro')]: deepseekEffort,
  [getModelMetadataKey('deepseek', 'deepseek-v4-flash')]: deepseekEffort,

  [getModelMetadataKey('kimi', 'kimi-k2.7-code')]: fixedThinkingCapability,
  [getModelMetadataKey('kimi', 'kimi-k2.7-code-highspeed')]: fixedThinkingCapability,
  [getModelMetadataKey('kimi', 'kimi-k2.6')]: toggleCapability('kimi'),
  [getModelMetadataKey('kimi', 'kimi-k2.5')]: toggleCapability('kimi'),
  [getModelMetadataKey('kimi', 'kimi-k2-thinking')]: toggleCapability('kimi'),

  [getModelMetadataKey('siliconflow', 'deepseek-ai/DeepSeek-V3.2')]: siliconflowToggleBudget,
  [getModelMetadataKey('siliconflow', 'deepseek-ai/DeepSeek-R1')]: siliconflowBudget,
  [getModelMetadataKey('siliconflow', 'deepseek-ai/DeepSeek-R1-Distill-Qwen-7B')]:
    siliconflowBudget,
  [getModelMetadataKey('siliconflow', 'Qwen/Qwen3-VL-32B-Instruct')]: siliconflowToggleBudget,
  [getModelMetadataKey('siliconflow', 'THUDM/GLM-4.1V-9B-Thinking')]: siliconflowBudget,
  [getModelMetadataKey('siliconflow', 'THUDM/GLM-Z1-Rumination-32B-0414')]: siliconflowBudget,

  [getModelMetadataKey('doubao', 'doubao-seed-2-0-pro-260215')]: doubaoSeed20Effort,
  [getModelMetadataKey('doubao', 'doubao-seed-2-0-lite-260215')]: doubaoSeed20Effort,
  [getModelMetadataKey('doubao', 'doubao-seed-2-0-mini-260215')]: doubaoSeed20Effort,
  [getModelMetadataKey('doubao', 'doubao-seed-1-8-251228')]: doubaoMode,

  [getModelMetadataKey('openrouter', 'deepseek/deepseek-v4-pro')]: effortCapability(
    'openrouter',
    ['low', 'medium', 'high'],
    'medium',
  ),
  [getModelMetadataKey('openrouter', 'deepseek/deepseek-v4-flash')]: effortCapability(
    'openrouter',
    ['low', 'medium', 'high'],
    'medium',
  ),

  [getModelMetadataKey('grok', 'grok-4.20-reasoning')]: fixedThinkingCapability,
  [getModelMetadataKey('grok', 'grok-4.20-multi-agent')]: fixedThinkingCapability,
  [getModelMetadataKey('grok', 'grok-4-1-fast-reasoning')]: fixedThinkingCapability,

  // dongniai-specific: MiniMax M3 (Hermes agent model) hardcoded disabled.
  // MiniMax-M3's behavior is unstable across thinking modes:
  //   - No param (MiniMax default) → thinking ON silently consumes all max_tokens on
  //     think block when prompts are long, leaving text='' for downstream parsers. ❌
  //   - { thinking: { type: 'enabled' } } → MiniMax returns empty content. ❌
  //   - { thinking: { type: 'disabled' } } → Returns valid JSON. ✅
  // Therefore we hardcode defaultMode='disabled' for ALL admin settings (default/enabled/disabled).
  // The admin's choice still affects other providers' thinking behavior, but for MiniMax-M3
  // we always pass disabled. This is a deliberate trade-off documented for the operator.
  // NOTE: We use 'anthropic' as the requestAdapter here (upstream uses 'minimax') because
  // dongniai routes MiniMax traffic through our internal adapter that speaks anthropic-style
  // thinking. This is an intentional divergence from upstream — see addons/dongniai/lib/ai.
  [getModelMetadataKey('minimax', 'MiniMax-M3')]: toggleCapability('anthropic', false),
  [getModelMetadataKey('minimax', 'MiniMax-M2.7')]: fixedThinkingCapability,

  [getModelMetadataKey('tencent-hunyuan', 'hy3-preview')]: hunyuanHy3Effort,

  [getModelMetadataKey('xiaomi', 'mimo-v2.5-pro')]: toggleCapability('xiaomi'),
  [getModelMetadataKey('xiaomi', 'mimo-v2-pro')]: toggleCapability('xiaomi'),
  [getModelMetadataKey('xiaomi', 'mimo-v2.5')]: toggleCapability('xiaomi'),
  [getModelMetadataKey('xiaomi', 'mimo-v2-omni')]: toggleCapability('xiaomi'),
  [getModelMetadataKey('xiaomi', 'mimo-v2-flash')]: toggleCapability('xiaomi'),

  // 【B 3 v7.7.6】lemonade 5 个本地 GGUF 模型 thinking capability
  [getModelMetadataKey('lemonade', 'Qwen3-4B-GGUF')]: lemonadeToggleBudget,
  [getModelMetadataKey('lemonade', 'Qwen3.5-4B-GGUF')]: lemonadeToggleBudget,
  [getModelMetadataKey('lemonade', 'Gemma-4-26B-A4B-it-GGUF')]: lemonadeToggleBudget,
  [getModelMetadataKey('lemonade', 'gpt-oss-20b')]: lemonadeToggleBudget,
  [getModelMetadataKey('lemonade', 'GPT-OSS-20B-GGUF')]: lemonadeToggleBudget,
};

export function getCatalogThinkingCapability(
  providerId: string,
  modelId: string,
): ThinkingCapability | undefined {
  const exact = THINKING_CAPABILITIES[getModelMetadataKey(providerId, modelId)];
  if (exact) return exact;

  // 【v6.97.138 P0 FIX 2026-09-15】MiniMax 家族归一化 — v6.97.125 KeyManager 重构后
  //   providerId 传的是 model_id ('MiniMax-M3') 而非 vendor 名 ('minimax')，
  //   exact 查不到 → 落到 openai-compat 兜底（非 M3 专属 anthropic-adapter-disabled）
  //   → 请求体 thinking 参数错误/缺失 → MiniMax API 默认思考 ON → 单 call 90~240s
  //   （老板实测回归：8 月同模型 40s → 9 月 11 分钟）。归一化后走注册表
  //   ['minimax','MiniMax-M3'] → toggleCapability('anthropic', false) → 强制 disabled。
  if (providerId === 'minimax' || /^MiniMax/i.test(providerId) || modelId.toLowerCase().includes('minimax')) {
    return toggleCapability('anthropic', false);
  }

  // 【B 3 v7.7.6】lemonade provider fallback (Phase C)
  if (providerId === 'lemonade') {
    return lemonadeToggleBudget;
  }

  // 【v6.97.125 2026-09-12 P0 修复】中文名 provider (智谱清言/通义千问/百炼 等) 不进注册表
  //   → THINKING_CAPABILITIES 查不到 → 返回 undefined → fetch wrapper 不注入 thinking:disabled
  //   → 模型默认 thinking ON → reasoning 吃光 tokens → content 空 → 30s+ 卡死
  //   真因实测: 智谱清言 glm-5.3-flash 短 prompt 10 tokens 全 reasoning, finish_reason=length
  // 修法: 中文名 provider 模糊匹配模型 ID 前缀 (glm/qwen/...) 走对应 toggleCapability
  //   - 智谱清言 + glm-* → toggleCapability('glm', false) 默认 disabled
  //   - 通义千问/百炼 + qwen-* → toggleCapability('qwen', false)
  //   - 其他中文 provider + 无法识别 → 默认 disabled (保守 — 不让 thinking 浪费 token)
  const modelLower = modelId.toLowerCase();
  if (providerId.includes('智谱') || providerId.includes('GLM') || providerId.includes('ZHIPU') || modelLower.includes('glm')) {
    return toggleCapability('glm', false);
  }
  if (providerId.includes('通义') || providerId.includes('千问') || providerId.includes('百炼') || modelLower.includes('qwen')) {
    return toggleCapability('qwen', false);
  }
  // 兜底: 任何未识别的 provider → 默认 disabled (保守 — reasoning 模型是少数)
  return toggleCapability('openai-compat', false);
}

export function applyModelMetadata(providers: Record<ProviderId, ProviderConfig>): void {
  for (const provider of Object.values(providers)) {
    for (const model of provider.models) {
      const thinking = getCatalogThinkingCapability(provider.id, model.id);
      if (thinking) {
        model.capabilities = {
          ...model.capabilities,
          thinking,
        };
      }
    }
  }
}
